import { flatten, groupBy } from 'lodash';
import React from 'react';
import moment from 'moment';

import { OverlayTrigger, Tooltip } from '../../../ui-kit';
import { formatTimeOnlyShort, isMultiDayEvent } from '../../../../utils/time';
import AvailabilityCodeIcon from '../../../general/AvailabilityCodeIcon';
import AvailabilityIcon from '../../../general/AvailabilityIcon';
import DutyFunctionIcon from '../../../general/DutyFunctionIcon';
import GroupName from '../../../general/GroupName';
import computeAvailabilityColors from '../../../../utils/availabilityColors';
import { IconCircleInfo } from '../../../ui-kit/icons';

// Group intervals by team, and remove duplication. For example, if a user is
// simultaneously a member of teams Red, Blue and Green, only 1 interval is
// rendered with the station name and "Red, Blue, Green".
const groupByTeam = (intervals, groupsById) => {
  intervals = flatten(intervals).filter(Boolean);
  intervals = removeStationIntervalsIfTeamMember(intervals);
  // intervals related to stations don't have to be grouped
  const result = intervals.filter(i => i.group.type !== 'team');

  const teamIntervals = intervals.filter(i => i.group.type === 'team');

  // group team intervals belonging to the same station
  const grouped = groupBy(teamIntervals, i => `${i.start_time}_${i.end_time}_${i.parentGroup && i.parentGroup.id}`);

  for (const intervals of Object.values(grouped)) {
    const group = groupsById[intervals[0].parentGroup.id];
    const mergedInterval = {
      ...intervals[0],
      group,
      teams: intervals.map(i => i.group)
    };
    result.push(mergedInterval);
  }

  return result;
};

export const defaultInterval = {
  start_time: '',
  end_time: '',
  available: true,
  active: true,
  exception_available: true,
  assigned_function_ids: [],
  availability_code_id: null
};

export const emptyInterval = {
  start_time: '',
  end_time: '',
  available: null,
  active: true,
  exception_available: null,
  assigned_function_ids: [],
  availability_code_id: null
};

// filter out all intervals for stations, if there is also an interval for
// the team
const removeStationIntervalsIfTeamMember = intervals => {
  const grouped = groupBy(intervals, interval => `${interval.start_time}_${interval.end_time}`);
  let result = [];
  for (let intervals of Object.values(grouped)) {
    intervals = intervals.filter(interval => {
      const { group } = interval;
      return !intervals.some(i => i.parentGroup && i.parentGroup.id === group.id && i.group.type === 'team');
    });

    result = [...result, ...intervals];
  }

  return result;
};

// Renders the time period indicator for an event, depending on the view
const renderTimeText = (event, view) => {
  if (view.type === 'dayGridMonth') {
    // Because there is very little space in month view, use a super short
    // version of the start & end time, with out the minutes if 0. Also remove
    // the spaces around the dash.
    const start = formatTimeOnlyShort(event.start, { showMinuteIfZero: false });
    const end = formatTimeOnlyShort(event.end, { showMinuteIfZero: false });
    return `${start}-${end}`;
  } else if (view.type.startsWith('list')) {
    // do not render in list mode, because it already has a separate column
    // for that
    return '';
  } else {
    return `${formatTimeOnlyShort(event.start)} - ${formatTimeOnlyShort(event.end)}`;
  }
};

// The event title which is shown in the calendar. For all views, this always consists of:
// - station + team name
// - duty function icon
// - availability code
const renderEventTitle = (event, view) => {
  const { group, comments, availabilityCode, dutyFunction, teams } = event.extendedProps;

  const stationName = <GroupName group={group} />;

  let commentElement;

  if (comments && view.type.startsWith('list')) {
    // in list mode, we show the comment below the title as a paragraph
    commentElement = (
      <p>
        <IconCircleInfo /> <i>{comments}</i>
      </p>
    );
  } else if (comments) {
    // in all other modes, the comment is shown as an info icon with tooltip
    const tooltip = <Tooltip id={event.id}>{comments}</Tooltip>;
    commentElement = (
      <OverlayTrigger trigger={['hover', 'focus']} placement="bottom" overlay={tooltip}>
        <IconCircleInfo />
      </OverlayTrigger>
    );
  }

  // If making changes to this, carefully watch the spaces in the different
  // views!
  return (
    <>
      <DutyFunctionIcon dutyFunction={dutyFunction} /> <AvailabilityCodeIcon code={availabilityCode} />{' '}
      <b>{stationName}</b>{' '}
      {(teams || []).map(t => (
        <React.Fragment key={t.id}>
          <GroupName group={t} />{' '}
        </React.Fragment>
      ))}
      {commentElement}
    </>
  );
};

// Custom event rendering. Reasons:
// - The default version doesn't allow for adding icons for the duty function
//   and availability code
// - the period text (time text) shows only the start time in month view
// See FullCalendar docs: https://fullcalendar.io/docs/content-injection
export const renderEventContent = ({ event, view }) => {
  const timeText = renderTimeText(event, view);
  const title = renderEventTitle(event, view);
  const { available, duty } = event.extendedProps;

  if (view.type === 'dayGridMonth' && !isMultiDayEvent(event.start, event.end)) {
    // Rationale for this code:
    // - if we're on the month view, we need to render the event as a single
    //   line. In order to do this, we need to remove some surrounding classes.
    // - However, if we're on the month view *and* the event is multi-day, we
    //   should render it the same as all other events. This is then
    //   automatically rendered correctly as a block with the right color
    // - add the type of availability as the AvailabilityIcon. Otherwise it is
    //   not possible to distinguish between on call or duty
    return (
      <>
        <div className="fc-event-time">
          <AvailabilityIcon available={available} standbyDuty={duty} /> {timeText}
        </div>
        <div className="fc-event-title fc-sticky">{title}</div>
      </>
    );
  } else if (view.type.startsWith('list')) {
    // - In the list view, we don't render the time, because is already
    //   shown in a separate comlumn
    // - The comments are rendered in a separate paragraph below the title
    return (
      <div className="fc-event-main-frame">
        <div className="fc-event-title-container">
          <div className="fc-event-title fc-sticky">{title}</div>
        </div>
      </div>
    );
  } else {
    return (
      <div className="fc-event-main-frame">
        <div style={{ fontSize: '0.8rem' }}>
          <div className="fc-event-time">{timeText}</div>
          <div className="fc-event-title-container">
            <div className="fc-event-title fc-sticky">{title}</div>
          </div>
        </div>
      </div>
    );
  }
};

export const viewTypeInitialRange = {
  timeGridWeek: {
    startStr: moment().startOf('week').format(),
    endStr: moment().startOf('week').add(1, 'week').format()
  },
  dayGridMonth: {
    startStr: moment().startOf('month').format(),
    endStr: moment().startOf('month').add(1, 'month').format()
  },
  timeGridDay: {
    startStr: moment().startOf('day').format(),
    endStr: moment().startOf('day').add(1, 'day').format()
  },
  listWeek: {
    startStr: moment().startOf('week').format(),
    endStr: moment().startOf('week').add(1, 'week').format()
  }
};

export const computeRelevantIntervals = (userSchedules, groupsById, functionsById, availabilityCodesById) =>
  groupByTeam(
    userSchedules.map(schedule =>
      schedule.intervals.map(interval => {
        const { active } = interval;
        if (!active) return; // if user is not plannable, skip

        const dutyFunction = interval.assigned_function_ids.map(id => functionsById[id])[0];
        const availabilityCode = availabilityCodesById[interval.availability_code_id];
        const group = groupsById[schedule.group_id];
        if (!group) return;

        const parentGroup = groupsById[group.parent_group_id];
        const duty = interval.assigned_function_ids.length > 0;
        const detailed_availability = interval.detailed_availability || {};
        const { recurring, exception } = detailed_availability;
        const hasException = exception === true || exception === false;
        const onlyRecurring = !duty && recurring && !hasException;

        return {
          ...interval,
          duty,
          recurring: onlyRecurring,
          group,
          parentGroup,
          dutyFunction,
          availabilityCode
        };
      })
    ),
    groupsById
  );

export const computeFullCalendarEvents = (canEdit, relevantIntervals, editMode, selectedGroupId) =>
  relevantIntervals
    .map(interval => [interval, computeAvailabilityColors(interval)])
    .filter(([_interval, colors]) => colors)
    .map(([interval, colors]) => ({
      id: `${interval.start_time}-${interval.end_time}`,
      start: interval.start_time,
      end: interval.end_time,
      editable: canEdit && selectedGroupId === interval.group.id && (interval.duty ? false : true),
      display: interval.recurring ? 'background' : null,
      // extendedProps is used to add extra properties. This is used here to
      // pass the code, function and parent station
      extendedProps: {
        obscured: editMode && selectedGroupId !== interval.group.id,
        duty: interval.duty,
        recurring: interval.recurring,
        available: interval.available,
        availabilityCode: interval.availabilityCode,
        dutyFunction: interval.dutyFunction,
        group: interval.group,
        comments: interval.comments,
        teams: interval.teams
      },
      ...colors
    }));
